import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

import type {
  PaginationEvent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  type FormFieldConfig,
  type SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

import { CouponIssuesService } from '@app/api/promotion-service/api/couponIssues.service';
import type { CouponIssueResponse } from '@app/api/promotion-service/model/couponIssueResponse';
import type { PageInfoCouponIssueResponse } from '@app/api/promotion-service/model/pageInfoCouponIssueResponse';
import { OfferService } from '@app/feature/promotion-management/services/offer.service';

type CouponIssueTableRow = CouponIssueResponse & {
  offerDisplayName?: string;
};

@Component({
  selector: 'app-coupon',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TooltipModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
  ],
  templateUrl: './coupon.component.html',
  styleUrl: './coupon.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CouponComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly api = inject(CouponIssuesService);
  private readonly offerService = inject(OfferService);
  private readonly router = inject(Router);

  private offerNameById = new Map<number, string>();

  readonly coupons = signal<CouponIssueTableRow[]>([]);
  readonly loading = signal(false);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(0);
  readonly pageSize = signal(20);
  readonly offerFilterOptions = signal<{ label: string; value: string }[]>([]);

  private readonly filters = signal<{
    issueNo?: string;
    offerId?: string;
  }>({});

  readonly searchConfig = signal<FormFieldConfig[]>([
    {
      key: 'issueNo',
      label: 'coupon.list.columns.code',
      type: 'text',
      placeholder: 'coupon.list.columns.code',
    },
    {
      key: 'offerId',
      label: 'coupon.list.columns.offer_name',
      type: 'select',
      placeholder: 'coupon.issue.placeholders.linked_offer',
      options: [],
      showClear: true,
    },
  ]);

  readonly defaultColumns = signal<TableColumn[]>([
    {
      prop: 'issueNo',
      label: 'coupon.list.columns.code',
      sortable: false,
      type: 'text',
      width: 140,
    },
    {
      prop: 'description',
      label: 'coupon.list.columns.name',
      sortable: false,
      type: 'text',
      width: 220,
    },
    {
      prop: 'offerDisplayName',
      label: 'coupon.list.columns.offer_name',
      sortable: false,
      type: 'text',
      width: 200,
    },
    {
      prop: 'totalIssuedCount',
      label: 'coupon.list.columns.total_issued',
      sortable: false,
      type: 'number',
      width: 120,
    },
    {
      prop: 'remainingUsableCount',
      label: 'coupon.list.columns.remaining_usable',
      sortable: false,
      type: 'number',
      width: 130,
    },
    {
      prop: 'actions',
      label: 'coupon.list.columns.actions',
      sortable: false,
      type: 'actions',
      width: 160,
    },
  ]);

  readonly flexibleColumns = signal<TableColumn[]>([]);

  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  ngOnInit(): void {
    this.loadOffersLookup();
    this.loadCouponIssues();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    const offerIdRaw = searchData['offerId'];
    const offerId =
      offerIdRaw === null || offerIdRaw === undefined || offerIdRaw === ''
        ? undefined
        : String(offerIdRaw);

    this.filters.set({
      issueNo: (searchData['issueNo'] as string | undefined)?.trim() || undefined,
      offerId,
    });
    this.currentPage.set(0);
    this.loadCouponIssues();
  }

  onReset(): void {
    this.filters.set({});
    this.currentPage.set(0);
    this.loadCouponIssues();
  }

  onPageChange(event: PaginationEvent): void {
    if (typeof event.page === 'number') {
      this.currentPage.set(event.page);
    }
    if (typeof event.rows === 'number') {
      this.pageSize.set(event.rows);
    }
    this.loadCouponIssues();
  }

  private loadOffersLookup(): void {
    this.offerService
      .getOffers({ page: 0, size: 1000 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ items }) => {
          const nameById = new Map<number, string>();
          const filterOptions: { label: string; value: string }[] = [];

          for (const offer of items ?? []) {
            const id = Number(offer.id);
            if (!Number.isFinite(id)) continue;
            const name = offer.displayName || offer.code || String(id);
            nameById.set(id, name);
            filterOptions.push({ label: name, value: String(id) });
          }

          this.offerNameById = nameById;
          this.offerFilterOptions.set(filterOptions);
          this.searchConfig.set([
            {
              key: 'issueNo',
              label: 'coupon.list.columns.code',
              type: 'text',
              placeholder: 'coupon.list.columns.code',
            },
            {
              key: 'offerId',
              label: 'coupon.list.columns.offer_name',
              type: 'select',
              placeholder: 'coupon.issue.placeholders.linked_offer',
              options: filterOptions,
              showClear: true,
            },
          ]);
          this.coupons.update((rows) => this.enrichRows(rows));
        },
        error: () => {
          this.offerNameById = new Map();
          this.offerFilterOptions.set([]);
        },
      });
  }

  private loadCouponIssues(): void {
    this.loading.set(true);
    const { issueNo, offerId } = this.filters();

    this.api
      .list1(
        {
          page: this.currentPage(),
          size: this.pageSize(),
          sortBy: 'id',
          ascending: false,
          issueNo,
          offerId,
        },
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (response: PageInfoCouponIssueResponse) => {
          this.coupons.set(this.enrichRows(response?.pageContent ?? []));
          this.totalRecords.set(response?.totalElements ?? 0);
        },
        error: (error: unknown) => {
          console.error('Error loading coupon issues:', error);
          this.coupons.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private enrichRows(rows: CouponIssueResponse[]): CouponIssueTableRow[] {
    return rows.map((row) => ({
      ...row,
      offerDisplayName: this.resolveOfferName(row.offerId),
    }));
  }

  private resolveOfferName(offerId: number | undefined): string {
    if (offerId == null) return '';
    const name = this.offerNameById.get(Number(offerId));
    return name ?? String(offerId);
  }

  onEdit(row: CouponIssueTableRow): void {
    if (!row?.id) return;
    this.router.navigate(['/marketing/coupon/edit', row.id]);
  }

  onViewIssuedCoupons(row: CouponIssueTableRow): void {
    if (!row?.id) return;
    this.router.navigate(['/marketing/coupon/view', row.id]);
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target || !this.advancedTable) return;

    const pButtonElement = target.closest('p-button');
    const buttonElement =
      (pButtonElement?.querySelector('button') as HTMLElement | null) ||
      target.closest('button') ||
      target;

    if (buttonElement) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  onAddCouponIssue(): void {
    this.router.navigate(['/marketing/coupon/add']);
  }
}
